export interface ImageMap {
  [id: string]: string; // base64 string (with data URI prefix)
}

class ImageRepositoryClass {
  private images: Map<string, HTMLImageElement> = new Map();

  async addImage(id: string, base64: string): Promise<void> {
    const img = await this.loadImage(base64);
    this.images.set(id, img);
  }

  async addImageFromUrl(
    id: string,
    url: string,
    options?: {
      crossOrigin?: "anonymous" | "use-credentials";
      persistAsDataUrl?: boolean;
      /** When persistAsDataUrl=true, sets fetch credentials; defaults derived from crossOrigin */
      fetchCredentials?: RequestCredentials; // "omit" | "same-origin" | "include"
    }
  ): Promise<void> {
    const { crossOrigin, persistAsDataUrl, fetchCredentials } = options ?? {};

    if (persistAsDataUrl) {
      // Fetch the remote image and convert to Data URL (requires CORS to allow reading the response)
      const dataUrl = await this.fetchToDataUrl(url, fetchCredentials ?? (crossOrigin === "use-credentials" ? "include" : "omit"));
      // Reuse existing base64 add to keep behavior identical
      await this.addImage(id, dataUrl);
      return;
    }

    // Store as URL directly (fast path)
    const img = await this.loadImageFromUrl(url, crossOrigin);
    this.images.set(id, img);
  }

  removeImage(id: string): void {
    this.images.delete(id);
  }

  getImage(id: string): HTMLImageElement | undefined {
    return this.images.get(id);
  }

  exportAsBase64Map(): ImageMap {
    const map: ImageMap = {};
    this.images.forEach((img, id) => {
      map[id] = img.src;
    });
    return map;
  }

  async importFromBase64Map(imageMap: ImageMap): Promise<void> {
    for (const [id, base64] of Object.entries(imageMap)) {
      await this.addImage(id, base64);
    }
  }

  clear(): void {
    this.images.clear();
  }

  private loadImage(base64: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = base64;

      if (base64.startsWith("data:image/svg+xml")) {
        img.decode?.()
          .then(() => resolve(img))
          .catch(() => resolve(img));
      }
    });
  }

  private loadImageFromUrl(
    url: string,
    crossOrigin?: "anonymous" | "use-credentials"
  ): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = crossOrigin;
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  private async fetchToDataUrl(
    url: string,
    credentials: RequestCredentials = "omit"
  ): Promise<string> {
    const res = await fetch(url, { credentials });
    if (!res.ok) {
      throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}



export const GlobalImageRepository = new ImageRepositoryClass();
